import React from 'react';
import { useReactiveVar } from '@apollo/client';
import { setItem } from '@cvent/apollo-client/utils/storage';
import { Menu } from '@cvent/carina/components/Menu';
import { ChevronDownIcon } from '@cvent/carina/components/Icon';
import { TextLink } from '@cvent/carina/components/TextLink';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { locales, LocaleToLanguage } from '@cvent/universal-login-web-localization/locale';
import { LOCALE_COOKIE } from '@cvent/universal-login-web-middleware';
import { useRouter } from 'next/router';

import { setCookie } from '@utils/RequestHelpers';
import { YEAR_IN_SECONDS } from '@constants/duration';
import { useFooterStyle } from '@hooks/style/useFooterStyle';
import { localeVar } from '@components/framework/ReactiveVars';
import { useBreakpoints } from '@cvent/carina/components/Breakpoints';
import { useCustomBreakpoints } from '@hooks/display/useCustomBreakpoints';

interface MenuOption {
  value: string;
  payload: () => void;
}

export const LanguageMenu = ({ testID }): JSX.Element => {
  const breakpoints = useBreakpoints();
  const { isMobile } = useCustomBreakpoints(breakpoints);
  const { footerButton } = useFooterStyle();
  const router = useRouter();
  const selectedLocale = useReactiveVar(localeVar);
  const menuOptions: Array<MenuOption> = [];

  const setLocale = (locale: string): void => {
    setCookie(LOCALE_COOKIE, locale, YEAR_IN_SECONDS);
    setItem('locale', locale);
    localeVar(locale);
    router.push({ pathname: router.pathname, query: { ...router.query } }, router.asPath, {
      locale,
      shallow: true
    });
  };

  for (const locale of Object.keys(locales)) {
    menuOptions.push({
      value: LocaleToLanguage[locale],
      payload: (): void => setLocale(locale)
    });
  }

  return (
    <Menu
      testID={`${testID}_languageMenu`}
      maxHeight={'192px'}
      options={menuOptions}
      placement={isMobile ? 'bottom' : 'right'}
      selected={selectedLocale}
      onSelect={(selection: MenuOption): void => selection.payload()}
      trigger={(handlePress): JSX.Element => (
        <TextLink onClick={handlePress} neutral target="_blank" testID={`${testID}_languageButton`} css={footerButton}>
          <Row>
            <Col width={'content'} padding={0}>
              {LocaleToLanguage[selectedLocale]}
              <ChevronDownIcon />
            </Col>
          </Row>
        </TextLink>
      )}
    />
  );
};
